/**
 * Batch 18-J12 — Provider Artifact Mapping Admin Panel
 *
 * Operator surface that maps a provider's external course IDs to
 * existing OptiBay training artifacts. Renders inside
 * `TrainingCatalogAdmin` under the `Provider Artifact Mapping` tab.
 * Backed by the J3 read RPCs + the J6 mapping upsert RPC.
 *
 * Scope:
 *   - provider selector via `list_training_providers_for_admin` (J3)
 *   - per-provider mapping list via
 *     `list_training_provider_artifact_mappings_for_admin` (J3)
 *   - create / edit mapping via
 *     `upsert_training_provider_artifact_mapping` (J6)
 *   - supporting unmatched-events summary via
 *     `list_training_provider_unmatched_events_for_admin` (J3)
 *
 * Out of scope (deferred to later J-lane batches):
 *   - user mapping UI
 *   - unresolved events full triage UI
 *   - audit history UI
 *   - provider registry CRUD
 *   - event reprocess action ("save and reprocess")
 *   - signed JWT key management
 *
 * Trust boundaries:
 *   - Mapping a course to an artifact is a routing/normalization
 *     step. It does NOT verify training completion, create provider
 *     attestation, or create manager verification.
 *   - The panel never reads provider tables directly; J3 read RPCs
 *     and the J6 mutation RPC are the only seams.
 *   - Raw provider event payload, payload hash, signature status,
 *     idempotency key, and HMAC secret material are never displayed.
 */

import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Loader2, Search, Pencil, Plus } from 'lucide-react';
import { useRecommendedTraining } from '@/hooks/useLearningCenter';

// ─── Narrow local RPC casts (generated Supabase types do not yet
//     know about the J3/J6 RPCs) ───────────────────────────────────────

export interface TrainingProviderAdminRow {
  provider_id: string;
  org_id: string;
  shop_id: string | null;
  provider_key: string;
  display_name: string;
  provider_type: string;
  auth_type: string;
  timestamp_tolerance_seconds: number;
  is_active: boolean;
  secret_configured: boolean;
  public_key_configured: boolean;
  created_at: string;
  updated_at: string;
  last_received_at: string | null;
  event_count: number | string;
  has_unmatched_events: boolean;
}

export interface TrainingProviderArtifactMappingAdminRow {
  mapping_id: string;
  provider_id: string;
  org_id: string;
  shop_id: string | null;
  external_course_id: string;
  external_course_key: string | null;
  external_course_url: string | null;
  training_artifact_id: string;
  training_artifact_title: string | null;
  training_artifact_type: string | null;
  training_artifact_is_active: boolean | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_seen_at: string | null;
  event_count: number | string;
}

export interface TrainingProviderUnmatchedEventAdminRow {
  event_id: string;
  provider_id: string;
  org_id: string;
  shop_id: string | null;
  provider_event_id: string;
  external_course_id: string | null;
  external_user_id: string | null;
  masked_external_user_email: string | null;
  event_type: string;
  event_status: string;
  processing_status: string;
  reconciliation_status: string | null;
  matched_artifact_id: string | null;
  matched_technician_profile_id: string | null;
  matched_user_id: string | null;
  received_at: string;
  attested_at: string | null;
  error_code: string | null;
  error_message: string | null;
}

interface ListProvidersRpc {
  (
    fn: 'list_training_providers_for_admin',
    args: {
      p_org_id: string;
      p_shop_id: string | null;
      p_include_inactive: boolean;
    },
  ): Promise<{
    data: TrainingProviderAdminRow[] | null;
    error: { message: string; code?: string } | null;
  }>;
}

interface ListMappingsRpc {
  (
    fn: 'list_training_provider_artifact_mappings_for_admin',
    args: {
      p_provider_id: string;
      p_include_inactive: boolean;
    },
  ): Promise<{
    data: TrainingProviderArtifactMappingAdminRow[] | null;
    error: { message: string; code?: string } | null;
  }>;
}

interface ListUnmatchedEventsRpc {
  (
    fn: 'list_training_provider_unmatched_events_for_admin',
    args: {
      p_provider_id: string;
      p_status_filter: string[] | null;
      p_limit: number;
      p_offset: number;
    },
  ): Promise<{
    data: TrainingProviderUnmatchedEventAdminRow[] | null;
    error: { message: string; code?: string } | null;
  }>;
}

interface UpsertMappingRpc {
  (
    fn: 'upsert_training_provider_artifact_mapping',
    args: {
      p_provider_id: string;
      p_external_course_id: string;
      p_training_artifact_id: string;
      p_is_active: boolean;
      p_actor_user_id: string;
      p_reason: string | null;
    },
  ): Promise<{
    data: unknown;
    error: { message: string; code?: string } | null;
  }>;
}

interface ProviderArtifactMappingAdminPanelProps {
  orgId: string | null | undefined;
  shopId: string | null | undefined;
  currentUserId: string | null | undefined;
}

// ─── Safe error mapper — never echoes raw Postgres error text. ─────

function safeRpcError(error: { message: string; code?: string } | null): string {
  if (!error) return 'Unknown error';
  if (error.code === '42501') {
    return 'You do not have permission to perform that action.';
  }
  const msg = (error.message ?? '').trim();
  return msg.length === 0 ? 'Action failed.' : msg;
}

// ═══════════════════════════════════════════════════════════════════
// Top-level panel
// ═══════════════════════════════════════════════════════════════════

export function ProviderArtifactMappingAdminPanel({
  orgId,
  shopId,
  currentUserId,
}: ProviderArtifactMappingAdminPanelProps) {
  const queryClient = useQueryClient();

  const [includeInactiveProviders, setIncludeInactiveProviders] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [includeInactiveMappings, setIncludeInactiveMappings] = useState(false);
  const [externalCourseSearch, setExternalCourseSearch] = useState('');
  const [artifactSearch, setArtifactSearch] = useState('');
  const [artifactTypeFilter, setArtifactTypeFilter] = useState<string>('all');

  const enabled = Boolean(orgId);

  // ── Provider list ────────────────────────────────────────────────
  const providersQuery = useQuery({
    queryKey: [
      'training-providers-admin',
      orgId ?? null,
      shopId ?? null,
      includeInactiveProviders,
    ],
    enabled,
    queryFn: async (): Promise<TrainingProviderAdminRow[]> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as ListProvidersRpc;
      const { data, error } = await rpc('list_training_providers_for_admin', {
        p_org_id: orgId as string,
        p_shop_id: shopId ?? null,
        p_include_inactive: includeInactiveProviders,
      });
      if (error) throw new Error(safeRpcError(error));
      return data ?? [];
    },
  });

  const selectedProvider = useMemo<TrainingProviderAdminRow | null>(() => {
    if (!selectedProviderId) return null;
    return (
      providersQuery.data?.find((p) => p.provider_id === selectedProviderId) ?? null
    );
  }, [providersQuery.data, selectedProviderId]);

  // ── Mapping list ─────────────────────────────────────────────────
  const mappingsQuery = useQuery({
    queryKey: [
      'training-provider-artifact-mappings-admin',
      selectedProviderId,
      includeInactiveMappings,
    ],
    enabled: Boolean(selectedProviderId),
    queryFn: async (): Promise<TrainingProviderArtifactMappingAdminRow[]> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as ListMappingsRpc;
      const { data, error } = await rpc(
        'list_training_provider_artifact_mappings_for_admin',
        {
          p_provider_id: selectedProviderId as string,
          p_include_inactive: includeInactiveMappings,
        },
      );
      if (error) throw new Error(safeRpcError(error));
      return data ?? [];
    },
  });

  const refetchMappings = () => {
    queryClient.invalidateQueries({
      queryKey: [
        'training-provider-artifact-mappings-admin',
        selectedProviderId,
        includeInactiveMappings,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [
        'training-providers-admin',
        orgId ?? null,
        shopId ?? null,
        includeInactiveProviders,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: ['training-provider-unmatched-events-summary', selectedProviderId],
    });
  };

  // ── Unmatched events summary ─────────────────────────────────────
  const unmatchedQuery = useQuery({
    queryKey: ['training-provider-unmatched-events-summary', selectedProviderId],
    enabled: Boolean(selectedProviderId),
    queryFn: async (): Promise<TrainingProviderUnmatchedEventAdminRow[]> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as ListUnmatchedEventsRpc;
      const { data, error } = await rpc(
        'list_training_provider_unmatched_events_for_admin',
        {
          p_provider_id: selectedProviderId as string,
          p_status_filter: null,
          p_limit: 100,
          p_offset: 0,
        },
      );
      if (error) throw new Error(safeRpcError(error));
      return data ?? [];
    },
  });

  // ── Artifact catalog (Option A: reuse useRecommendedTraining) ────
  const artifactsQuery = useRecommendedTraining({ pageSize: 500 });
  const artifacts = artifactsQuery.data?.data ?? [];

  // ── Dialog state ─────────────────────────────────────────────────
  const [dialogState, setDialogState] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; mapping: TrainingProviderArtifactMappingAdminRow }
    | null
  >(null);
  const [formExternalCourseId, setFormExternalCourseId] = useState('');
  const [formArtifactId, setFormArtifactId] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [formReason, setFormReason] = useState('');
  const [formArtifactSearch, setFormArtifactSearch] = useState('');
  const [originalArtifactId, setOriginalArtifactId] = useState('');
  const [originalIsActive, setOriginalIsActive] = useState(true);
  const [originalExternalCourseId, setOriginalExternalCourseId] = useState('');

  useEffect(() => {
    if (!dialogState) return;
    if (dialogState.mode === 'create') {
      setFormExternalCourseId('');
      setFormArtifactId('');
      setFormIsActive(true);
      setFormReason('');
      setFormArtifactSearch('');
      setOriginalArtifactId('');
      setOriginalIsActive(true);
      setOriginalExternalCourseId('');
    } else {
      setFormExternalCourseId(dialogState.mapping.external_course_id);
      setFormArtifactId(dialogState.mapping.training_artifact_id);
      setFormIsActive(dialogState.mapping.is_active);
      setFormReason('');
      setFormArtifactSearch('');
      setOriginalArtifactId(dialogState.mapping.training_artifact_id);
      setOriginalIsActive(dialogState.mapping.is_active);
      setOriginalExternalCourseId(dialogState.mapping.external_course_id);
    }
  }, [dialogState]);

  const upsertMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProviderId) throw new Error('Select a provider first.');
      if (!currentUserId) throw new Error('Sign in required.');
      if (formExternalCourseId.trim().length === 0) {
        throw new Error('External course ID is required.');
      }
      if (!formArtifactId) {
        throw new Error('Training artifact is required.');
      }
      const rpc = supabase.rpc.bind(supabase) as unknown as UpsertMappingRpc;
      const { error } = await rpc('upsert_training_provider_artifact_mapping', {
        p_provider_id: selectedProviderId,
        p_external_course_id: formExternalCourseId.trim(),
        p_training_artifact_id: formArtifactId,
        p_is_active: formIsActive,
        p_actor_user_id: currentUserId,
        p_reason: formReason.trim() || null,
      });
      if (error) throw new Error(safeRpcError(error));
    },
    onSuccess: () => {
      toast.success('Artifact mapping saved.');
      refetchMappings();
      setDialogState(null);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  // ── Reason-required logic for edits ──────────────────────────────
  const isEdit = dialogState?.mode === 'edit';
  const artifactChanged = isEdit && formArtifactId !== originalArtifactId;
  const deactivating = isEdit && originalIsActive && !formIsActive;
  const externalCourseChanged =
    isEdit && formExternalCourseId.trim() !== originalExternalCourseId;
  const reasonRequired =
    artifactChanged || deactivating || externalCourseChanged;

  // ── Filtered mappings ────────────────────────────────────────────
  const visibleMappings = useMemo(() => {
    const rows = mappingsQuery.data ?? [];
    const ec = externalCourseSearch.trim().toLowerCase();
    const at = artifactSearch.trim().toLowerCase();
    return rows.filter((m) => {
      if (artifactTypeFilter !== 'all' && m.training_artifact_type !== artifactTypeFilter) {
        return false;
      }
      if (ec && !m.external_course_id.toLowerCase().includes(ec)) return false;
      if (at) {
        const title = (m.training_artifact_title ?? '').toLowerCase();
        if (!title.includes(at)) return false;
      }
      return true;
    });
  }, [mappingsQuery.data, externalCourseSearch, artifactSearch, artifactTypeFilter]);

  const distinctArtifactTypes = useMemo(() => {
    const rows = mappingsQuery.data ?? [];
    const set = new Set<string>();
    for (const r of rows) {
      if (r.training_artifact_type) set.add(r.training_artifact_type);
    }
    return Array.from(set).sort();
  }, [mappingsQuery.data]);

  // ── Unmatched-events summary derivations ────────────────────────
  const unmatchedSummary = useMemo(() => {
    const rows = unmatchedQuery.data ?? [];
    const unmatched = rows.filter((e) => e.matched_artifact_id === null);
    const byCourse = new Map<string, { count: number; lastSeen: string | null }>();
    for (const e of unmatched) {
      if (!e.external_course_id) continue;
      const prev = byCourse.get(e.external_course_id);
      if (prev) {
        prev.count += 1;
        if (!prev.lastSeen || (e.received_at && e.received_at > prev.lastSeen)) {
          prev.lastSeen = e.received_at;
        }
      } else {
        byCourse.set(e.external_course_id, {
          count: 1,
          lastSeen: e.received_at,
        });
      }
    }
    const mappedCourseIds = new Set(
      (mappingsQuery.data ?? []).map((m) => m.external_course_id),
    );
    const topCourses = Array.from(byCourse.entries())
      .filter(([id]) => !mappedCourseIds.has(id))
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 5)
      .map(([id, info]) => ({ external_course_id: id, count: info.count }));
    const lastReceived = rows.reduce<string | null>((acc, e) => {
      if (!acc) return e.received_at;
      return e.received_at > acc ? e.received_at : acc;
    }, null);
    return {
      totalUnmatched: unmatched.length,
      topCourses,
      lastReceived,
    };
  }, [unmatchedQuery.data, mappingsQuery.data]);

  const hasStaleMappings = unmatchedSummary.topCourses.length > 0;

  // ── Provider options ─────────────────────────────────────────────
  const providerOptions = providersQuery.data ?? [];

  // ── Render branches ──────────────────────────────────────────────

  if (!enabled) {
    return (
      <Card>
        <CardContent
          className="py-12 text-center text-sm text-muted-foreground"
          data-testid="provider-artifact-mapping-admin-panel"
        >
          You do not have access to provider artifact mapping admin.
        </CardContent>
      </Card>
    );
  }

  return (
    <div
      className="space-y-4"
      data-testid="provider-artifact-mapping-admin-panel"
    >
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Provider Artifact Mapping</CardTitle>
          <CardDescription>
            Bind provider external course IDs to OptiBay training artifacts.
            Mapping is a routing step — it does not verify training completion
            or create provider attestation.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[260px]">
              <Label htmlFor="provider-artifact-mapping-provider-select" className="text-xs">
                Provider
              </Label>
              <select
                id="provider-artifact-mapping-provider-select"
                data-testid="provider-artifact-mapping-provider-select"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedProviderId ?? ''}
                onChange={(e) => setSelectedProviderId(e.target.value || null)}
              >
                <option value="">Select a provider…</option>
                {providerOptions.map((p) => (
                  <option
                    key={p.provider_id}
                    value={p.provider_id}
                    data-testid={`provider-artifact-mapping-provider-option-${p.provider_id}`}
                  >
                    {p.display_name} — {p.provider_key} ({p.auth_type})
                    {!p.is_active ? ' [inactive]' : ''}
                    {p.has_unmatched_events ? ' [unmatched events]' : ''}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                data-testid="provider-artifact-mapping-include-inactive-providers"
                checked={includeInactiveProviders}
                onCheckedChange={(v) => setIncludeInactiveProviders(Boolean(v))}
              />
              Include inactive providers
            </label>
          </div>

          {providersQuery.isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading providers…
            </div>
          )}
          {providersQuery.error && (
            <div
              className="text-sm text-muted-foreground"
              data-testid="provider-artifact-mapping-provider-error"
            >
              {/permission|42501/i.test(
                (providersQuery.error as Error).message,
              )
                ? 'You do not have access to provider artifact mapping.'
                : 'Unable to load providers right now.'}
            </div>
          )}
          {!providersQuery.isLoading &&
            !providersQuery.error &&
            providerOptions.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No providers configured yet. Use the Training Providers tab to
                seed a provider first.
              </div>
            )}
        </CardContent>
      </Card>

      {!selectedProviderId ? (
        <Card>
          <CardContent
            className="py-12 text-center text-sm text-muted-foreground"
            data-testid="provider-artifact-mapping-no-provider-selected"
          >
            Select a provider to view artifact mappings.
          </CardContent>
        </Card>
      ) : (
        <>
          {selectedProvider && !selectedProvider.is_active && (
            <div
              className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm flex items-start gap-2"
              data-testid="provider-artifact-mapping-inactive-provider-warning"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <span>
                This provider is inactive. Mapping edits are allowed, but
                provider callbacks may not be accepted until the provider is
                reactivated.
              </span>
            </div>
          )}

          {hasStaleMappings && (
            <div
              className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm flex items-start gap-2"
              data-testid="provider-artifact-mapping-stale-mappings-warning"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <span>
                Some external course IDs have unmatched events but no mapping.
                Create or update mappings below to route future events.
              </span>
            </div>
          )}

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-base">Artifact mappings</CardTitle>
                <Button
                  size="sm"
                  data-testid="provider-artifact-mapping-create-action"
                  onClick={() => setDialogState({ mode: 'create' })}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add mapping
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="provider-artifact-mapping-search-external-course"
                    placeholder="Search external course ID…"
                    value={externalCourseSearch}
                    onChange={(e) => setExternalCourseSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    data-testid="provider-artifact-mapping-search-artifact"
                    placeholder="Search artifact title…"
                    value={artifactSearch}
                    onChange={(e) => setArtifactSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {distinctArtifactTypes.length > 0 && (
                  <Select
                    value={artifactTypeFilter}
                    onValueChange={(v) => setArtifactTypeFilter(v)}
                  >
                    <SelectTrigger
                      className="w-[180px]"
                      data-testid="provider-artifact-mapping-artifact-type-filter"
                    >
                      <SelectValue placeholder="Artifact type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All artifact types</SelectItem>
                      {distinctArtifactTypes.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    data-testid="provider-artifact-mapping-include-inactive-mappings"
                    checked={includeInactiveMappings}
                    onCheckedChange={(v) => setIncludeInactiveMappings(Boolean(v))}
                  />
                  Include inactive mappings
                </label>
              </div>

              {mappingsQuery.isLoading && (
                <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading mappings…
                </div>
              )}

              {mappingsQuery.error && (
                <div
                  className="py-8 text-center text-sm text-muted-foreground"
                  data-testid="provider-artifact-mapping-list-error"
                >
                  {/permission|42501/i.test(
                    (mappingsQuery.error as Error).message,
                  )
                    ? 'You do not have access to provider artifact mapping.'
                    : 'Unable to load mappings right now.'}
                </div>
              )}

              {!mappingsQuery.isLoading &&
                !mappingsQuery.error &&
                visibleMappings.length === 0 && (
                  <div
                    className="py-8 text-center text-sm text-muted-foreground"
                    data-testid="provider-artifact-mapping-empty"
                  >
                    No artifact mappings yet for this provider.
                  </div>
                )}

              {visibleMappings.length > 0 && (
                <ul
                  data-testid="provider-artifact-mapping-list"
                  className="space-y-2"
                >
                  {visibleMappings.map((m) => (
                    <li
                      key={m.mapping_id}
                      data-testid="provider-artifact-mapping-row"
                      className="rounded-lg border bg-card p-3 flex flex-wrap items-start justify-between gap-3"
                    >
                      <div className="space-y-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <code
                            data-testid="provider-artifact-mapping-external-course"
                            className="font-mono text-sm"
                          >
                            {m.external_course_id}
                          </code>
                          <span className="text-xs text-muted-foreground">→</span>
                          <span
                            data-testid="provider-artifact-mapping-artifact"
                            className="text-sm font-medium"
                          >
                            {m.training_artifact_title ?? m.training_artifact_id}
                          </span>
                          {m.training_artifact_type && (
                            <Badge variant="outline" className="text-xs">
                              {m.training_artifact_type}
                            </Badge>
                          )}
                          <Badge
                            variant={m.is_active ? 'default' : 'outline'}
                            data-testid="provider-artifact-mapping-status"
                            className="text-xs"
                          >
                            {m.is_active ? 'Mapping active' : 'Mapping inactive'}
                          </Badge>
                        </div>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                          <div className="flex gap-2">
                            <dt>Created at:</dt>
                            <dd>{m.created_at}</dd>
                          </div>
                          <div className="flex gap-2">
                            <dt>Updated at:</dt>
                            <dd>{m.updated_at}</dd>
                          </div>
                        </dl>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="provider-artifact-mapping-edit-action"
                          onClick={() => setDialogState({ mode: 'edit', mapping: m })}
                        >
                          <Pencil className="h-4 w-4 mr-1" /> Edit
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {unmatchedQuery.data && unmatchedQuery.data.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Unmatched events</CardTitle>
                <CardDescription>
                  Provider events that have not yet matched an OptiBay artifact.
                  Click an external course ID to prefill a new mapping.
                </CardDescription>
              </CardHeader>
              <CardContent
                className="space-y-2 text-sm"
                data-testid="provider-artifact-mapping-unmatched-events-summary"
              >
                <div className="flex flex-wrap items-center gap-4">
                  <span>
                    Total unmatched: <strong>{unmatchedSummary.totalUnmatched}</strong>
                  </span>
                  {unmatchedSummary.lastReceived && (
                    <span className="text-xs text-muted-foreground">
                      Last received: {unmatchedSummary.lastReceived}
                    </span>
                  )}
                </div>
                {unmatchedSummary.topCourses.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                      Suggested from unmatched events:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {unmatchedSummary.topCourses.map((c) => (
                        <Button
                          key={c.external_course_id}
                          size="sm"
                          variant="outline"
                          data-testid="provider-artifact-mapping-suggested-from-unmatched"
                          onClick={() => {
                            setDialogState({ mode: 'create' });
                            // Defer setting external course until the
                            // dialog's reset effect runs.
                            setTimeout(() => {
                              setFormExternalCourseId(c.external_course_id);
                            }, 0);
                          }}
                        >
                          <span className="font-mono text-xs">
                            {c.external_course_id}
                          </span>
                          <span className="ml-2 text-xs text-muted-foreground">
                            {c.count}
                          </span>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Create / edit dialog */}
      <Dialog
        open={dialogState !== null}
        onOpenChange={(open) => {
          if (!open) setDialogState(null);
        }}
      >
        <DialogContent data-testid="provider-artifact-mapping-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialogState?.mode === 'edit'
                ? 'Edit artifact mapping'
                : 'Add artifact mapping'}
            </DialogTitle>
            <DialogDescription>
              Bind a provider external course ID to an OptiBay training
              artifact. Mapping is a routing step — it does not create
              attestation or technician completion.
            </DialogDescription>
          </DialogHeader>

          {selectedProvider && (
            <div className="rounded-md border bg-muted/30 p-2 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground">Provider:</span>
                <span className="font-medium">{selectedProvider.display_name}</span>
                <code className="font-mono text-muted-foreground">
                  {selectedProvider.provider_key}
                </code>
                <Badge variant="secondary" className="text-xs">
                  {selectedProvider.auth_type}
                </Badge>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="provider-artifact-mapping-external-course-input">
              External course ID
              <span className="text-destructive"> *</span>
            </Label>
            <Input
              id="provider-artifact-mapping-external-course-input"
              data-testid="provider-artifact-mapping-external-course-input"
              placeholder="provider-course-123"
              value={formExternalCourseId}
              onChange={(e) => setFormExternalCourseId(e.target.value)}
              autoComplete="off"
            />
            {dialogState?.mode === 'edit' &&
              formExternalCourseId.trim() !== originalExternalCourseId && (
                <p className="text-xs text-amber-600">
                  Changing the external course ID rebinds this mapping to a
                  different provider course.
                </p>
              )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="provider-artifact-mapping-artifact-search">
              OptiBay training artifact
              <span className="text-destructive"> *</span>
            </Label>
            <Input
              id="provider-artifact-mapping-artifact-search"
              data-testid="provider-artifact-mapping-artifact-search"
              placeholder="Search artifacts by title…"
              value={formArtifactSearch}
              onChange={(e) => setFormArtifactSearch(e.target.value)}
              autoComplete="off"
            />
            <div className="max-h-48 overflow-auto rounded-md border">
              {artifacts
                .filter((a) => {
                  const q = formArtifactSearch.trim().toLowerCase();
                  if (!q) return true;
                  return a.title.toLowerCase().includes(q);
                })
                .slice(0, 50)
                .map((a) => (
                  <label
                    key={a.id}
                    className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-accent"
                    data-testid={`provider-artifact-mapping-artifact-option-${a.id}`}
                  >
                    <input
                      type="radio"
                      name="provider-artifact-mapping-artifact-id"
                      value={a.id}
                      checked={formArtifactId === a.id}
                      onChange={() => setFormArtifactId(a.id)}
                    />
                    <span className="flex-1 truncate">{a.title}</span>
                    <Badge variant="outline" className="text-xs">
                      {a.artifact_type}
                    </Badge>
                    {!a.is_active && (
                      <Badge variant="outline" className="text-xs">
                        Inactive
                      </Badge>
                    )}
                  </label>
                ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Switch
              data-testid="provider-artifact-mapping-active-toggle"
              checked={formIsActive}
              onCheckedChange={(v) => setFormIsActive(Boolean(v))}
            />
            {formIsActive ? 'Mapping active' : 'Mapping inactive'}
          </label>

          <div className="space-y-2">
            <Label htmlFor="provider-artifact-mapping-reason">
              Reason
              {reasonRequired && <span className="text-destructive"> *</span>}
            </Label>
            <Textarea
              id="provider-artifact-mapping-reason"
              data-testid="provider-artifact-mapping-reason"
              placeholder="Operational note (visible in the admin audit log)"
              value={formReason}
              onChange={(e) => setFormReason(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button
              data-testid="provider-artifact-mapping-confirm"
              disabled={
                upsertMutation.isPending ||
                formExternalCourseId.trim().length === 0 ||
                !formArtifactId ||
                (reasonRequired && formReason.trim().length === 0)
              }
              onClick={() => upsertMutation.mutate()}
            >
              {upsertMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              )}
              {dialogState?.mode === 'edit' ? 'Save mapping' : 'Create mapping'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
